import { validateNotEmpty } from "@common/validation";
import { Location } from "@services/locations/types";
import { ValidationResult } from "./types";
import { get, isArray, set } from "lodash";
import { format, isBefore, isEqual, set as setDate } from "date-fns";
import { dayOrder } from "@common/utils/recurrence";

const requiredFields: string[] = [
  "name",
  "details.address1",
  "city",
  "country",
  "details.timeZone",
  "account.id",
];

const nonAutomaticGeoRequiredFields: string[] = ["longitude", "latitude"];

const checkRequired = (
  location: Location | undefined,
  resultObject: ValidationResult,
  key: string
) => {
  const value = get(location, key);
  if (!value || !validateNotEmpty(value)) {
    set(resultObject.errors, key, "Required");
    resultObject.isValid = false;
  }
  if (key === "latitude" || key === "longitude") {
    if (!checkCoordinate(key, value)) {
      set(resultObject.errors, key, "Required");
      resultObject.isValid = false;
    }
  }
};

export const validateLocation = (
  location: Location | undefined
): ValidationResult => {
  const result: ValidationResult = {
    isValid: true,
    errors: { account: {}, details: {} },
  };
  if ((location?.details?.schedule?.daysOfWeek?.length ?? 0) > 0) {
    result.isValid = timeIntervalIsValid(
      location?.details?.schedule?.from,
      location?.details?.schedule?.to,
      location?.details?.schedule?.always24h
    );
  }
  for (let key of requiredFields) {
    checkRequired(location, result, key);
  }

  if (location?.details?.geoLocationAutomatic === undefined) {
    set(result.errors, "details.geoLocationAutomatic", "Required");
    result.isValid = false;
  }

  if (location?.details?.geoLocationAutomatic === false) {
    for (let key of nonAutomaticGeoRequiredFields) {
      checkRequired(location, result, key);
    }
  }

  return result;
};

export const checkCoordinate = (
  coordinate: "latitude" | "longitude",
  value: number | undefined
): boolean => {
  const limits = coordinate === "longitude" ? 180 : 90;
  if (value === undefined || String(value) === "") return true;
  const validCoordinate = /^-?\d+(\.\d+)?$/;
  if (
    isNaN(value) ||
    !validCoordinate.test(String(value)) ||
    Math.abs(value) > limits ||
    (coordinate === "longitude" && value === limits)
  ) {
    return false;
  }
  return true;
};

export const mappedDaysOfWeek = (openDaysOfWeek?: number[]) => {
  return openDaysOfWeek?.map((index) => dayOrder[index - 1]);
};

export const formatTimeStringToTime = (time?: string): string | undefined => {
  const date = new Date();
  if (time) {
    const [hours, minutes] = time.split(":").map((t) => Number(t));
    date.setHours(hours, minutes, 0, 0);
    return format(date, "h:mm a");
  } else {
    return undefined;
  }
};

export const timeIntervalIsValid = (
  startTime?: string,
  endTime?: string,
  open24h?: boolean
): boolean => {
  if (open24h) return true;
  if (!startTime && !endTime) return false;
  if (!startTime || !endTime) return false;
  const start = timeStringToDate(startTime);
  const end = timeStringToDate(endTime);
  if (start && end) {
    return true;
  }

  return false;
};

export const isOpen24hours = (
  startTime?: string,
  endTime?: string
): boolean => {
  if (!startTime || !endTime) return false;

  const startDateTime = timeStringToDate(startTime);
  const endDateTime = timeStringToDate(endTime);

  if (startDateTime === null || endDateTime === null) return false;

  return (
    startDateTime.getHours() === 0 &&
    startDateTime.getMinutes() === 0 &&
    endDateTime.getHours() === 23 &&
    endDateTime.getMinutes() === 59 &&
    endDateTime?.getSeconds() === 59
  );
};

export const timeStringToDate = (time?: string) => {
  if (!time) return null;
  const [hours, minutes, seconds] = time?.split(":").map((t) => Number(t));
  return setDate(new Date(), { hours, minutes, seconds, milliseconds: 0 });
};
export const dateToTimeString = (date: Date): string => {
  return format(date, "HH:mm:ss");
};
